import { FormField, FormService } from "@shared/services/FormServices";
import { normalizeDate } from "@shared/utils/dateUtils";
import { useEffect, useMemo, useState, useRef } from "react";
import { differenceInDays, format } from "date-fns";
import { AlertTriangle, Droplets, Gauge, Info, Zap, Check } from "lucide-react";
import { Switch } from "@shared/ui/switch";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { DynamicForm } from "@shared/components/DynamicForm";
import { cn } from "@shared/lib/utils";
import { Separator } from "@shared/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useChoices, usePatchBillCorrection } from "../../hook";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";

const MeterCorrectionCard = ({
  meter,
  index,
  totalMeters,
  billingCycleDays,
  isCorrecting,
  onToggleCorrection,
  onFormReady,
  savedMeterData,
  onFormEdit,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  
  // Local state for tracking field values
  const [meterStatus, setMeterStatus] = useState<string>("0");
  const [currentReading, setCurrentReading] = useState<number | null>(null);
  const [readingDate, setReadingDate] = useState<Date | null>(null);
  const [calculatedConsumption, setCalculatedConsumption] = useState<number | null>(null);
  const [serviceDays, setServiceDays] = useState<number | null>(null);
  const [isServiceDaysExceeded, setIsServiceDaysExceeded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const { data: statusChoices } = useChoices('meter_reading_status', remoteUtilityId);
  const readingStatus = mapToLabelValue(statusChoices);
  
  // Ref to prevent infinite loops
  const isUpdatingRef = useRef(false);

  const getFormFields = (meter): FormField[] => {
    return [
      {
        name: "status",
        label: "Reading Status",
        type: "select",
        required: true,
        options: readingStatus,
        placeholder: "Select status",
        className: "col-span-1",
      },
      {
        name: "correctedCurrent",
        label: "Current Reading",
        type: "decimal",
        required: true,
        defaultValue: "",
        placeholder: "Enter reading",
        className: "col-span-1",
        disabled: meterStatus === "2", //2 = rcnt
      },
      {
        name: "readingDate",
        label: "Reading Date",
        type: "datepicker",
        required: true,
        className: "col-span-1",
      },
      {
        name: "consumption",
        label: "Consumption",
        type: "number",
        required: true,
        className: "col-span-1",
        disabled: true,
      },
      {
        name: "serviceDays",
        label: "Service Days",
        type: "number",
        required: true,
        className: "col-span-1",
        disabled: true,
      },
      {
        name: "reason",
        label: "Reason for Correction",
        type: "text",
        required: true,
        placeholder: "Enter reason...",
        className: "col-span-5",
      },
    ];
  };

  const form = FormService.createForm({
    fields: getFormFields(meter),
    mode: "onChange",
    validateOnMount: false,
  });

  // Manual watch for fields that DynamicForm doesn't trigger onFieldChange for
  const watchedCurrent = form.watch("correctedCurrent");
  const watchedDate = form.watch("readingDate");

  useEffect(() => {
    if (isInitialized && !isUpdatingRef.current) {
      handleFieldChange("correctedCurrent", watchedCurrent);
    }
  }, [watchedCurrent, isInitialized]);

  useEffect(() => {
    if (isInitialized && !isUpdatingRef.current) {
      handleFieldChange("readingDate", watchedDate);
    }
  }, [watchedDate, isInitialized]);

  // Initialize form when component mounts or when savedMeterData changes
  useEffect(() => {
    onFormReady(meter.id, form);
    
    isUpdatingRef.current = true;
    
    if (savedMeterData) {
      // Populate from saved data
      form.setValue("status", savedMeterData.reading_status || "normal", { shouldValidate: false });
      form.setValue("correctedCurrent", savedMeterData.corrected_reading ?? "", { shouldValidate: false });
      form.setValue("readingDate", savedMeterData.reading_date, { shouldValidate: false });
      form.setValue("consumption", savedMeterData.consumption || 0, { shouldValidate: false });
      form.setValue("serviceDays", savedMeterData.serviceDays || 0, { shouldValidate: false });
      form.setValue("reason", savedMeterData.reason || "", { shouldValidate: false });
      
      // Set local states
      setMeterStatus(savedMeterData.reading_status || "normal");
      setCurrentReading(savedMeterData.corrected_reading);
      setReadingDate(savedMeterData.reading_date);
      setCalculatedConsumption(savedMeterData.consumption || 0);
      setServiceDays(savedMeterData.serviceDays || 0);
      setIsServiceDaysExceeded((savedMeterData.serviceDays || 0) > billingCycleDays);
    } else {
      // Set initial default values for fresh form
      form.setValue("status", "normal", { shouldValidate: false });
    }
    
    isUpdatingRef.current = false;
    
    // Mark as initialized after a short delay to ensure all setValue calls complete
    setTimeout(() => {
      setIsInitialized(true);
    }, 100);
  }, [savedMeterData]);

  // Calculate consumption based on current reading
  const calculateConsumption = (status: string, correctedCurrent: number | null) => {
    if (status === "2") {
      return 0;
    }
    
    if (correctedCurrent !== null && !isNaN(correctedCurrent)) {
      return correctedCurrent - meter.prevReading;
    }
    
    return null;
  };

  // Calculate service days based on reading date
  const calculateServiceDays = (newReadingDate: Date | null) => {
    if (!newReadingDate) {
      return null;
    }
    
    try {
      const prevDate = normalizeDate(meter.prevReadDate);
      const currDate = new Date(newReadingDate);
      return differenceInDays(currDate, prevDate);
    } catch (error) {
      console.error("Error calculating service days:", error);
      return null;
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    // Prevent infinite loops
    if (isUpdatingRef.current) {
      return;
    }

    // Trigger edit callback when field changes (only after initialization)
    if (isInitialized && savedMeterData && onFormEdit) {
      onFormEdit();
    }

    switch (fieldName) {
      case "status": {
        setMeterStatus(value);

        if (value === "2") {
          // For RCNT, set current reading to original current reading
          const correctedReading = Number(meter.currReading);
          setCurrentReading(correctedReading);
          setCalculatedConsumption(0);
          
          isUpdatingRef.current = true;
          form.setValue("correctedCurrent", correctedReading, { shouldValidate: false });
          form.setValue("consumption", 0, { shouldValidate: false });
          isUpdatingRef.current = false;
        } else {
          // Recalculate consumption with current reading if available
          if (currentReading !== null) {
            const consumption = calculateConsumption(value, currentReading);
            setCalculatedConsumption(consumption);
            
            isUpdatingRef.current = true;
            form.setValue("consumption", consumption ?? 0, { shouldValidate: false });
            isUpdatingRef.current = false;
          }
        }
        break;
      }

      case "correctedCurrent": {
        const reading = value ? Number(value) : null;
        setCurrentReading(reading);
        
        // Calculate consumption
        const consumption = calculateConsumption(meterStatus, reading);
        setCalculatedConsumption(consumption);
        
        // Update consumption field
        isUpdatingRef.current = true;
        form.setValue("consumption", consumption ?? 0, { shouldValidate: false });
        isUpdatingRef.current = false;
        break;
      }

      case "readingDate": {
        setReadingDate(value);
        
        // Calculate service days
        const days = calculateServiceDays(value);
        setServiceDays(days);
        
        // Check if service days exceed billing cycle
        const exceeded = days !== null && days > billingCycleDays;
        setIsServiceDaysExceeded(exceeded);
        
        // Update service days field
        isUpdatingRef.current = true;
        form.setValue("serviceDays", days ?? 0, { shouldValidate: false });
        isUpdatingRef.current = false;
        break;
      }

      default:
        break;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {meter.type === "Electricity" ? (
            <Zap className="h-5 w-5 text-yellow-600" />
          ) : (
            <Droplets className="h-5 w-5 text-blue-600" />
          )}
          <h3 className="font-bold text-gray-900 text-base">
            {meter.type} Meter{" "}
            <span className="text-gray-500 font-normal text-sm">
              ({meter.meterNo})
            </span>
          </h3>
        </div>
        <div
          className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200 cursor-pointer"
          onClick={() => onToggleCorrection(meter.id, !isCorrecting)}
        >
          <Switch
            checked={isCorrecting}
            onCheckedChange={(checked) => onToggleCorrection(meter.id, checked)}
          />
          <span className="text-sm font-medium text-gray-700">
            Correct Reading
          </span>
        </div>
      </div>

      {/* Current Bill Details (Read Only) */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-4 p-4 bg-gray-50/50 rounded-lg border border-gray-100">
        <ValueLabel label="Previous Reading" value={meter.prevReading} />
        <ValueLabel label="Prev Read Date" value={meter.prevReadDate} />
        <ValueLabel label="Current Reading" value={meter.currReading} />
        <ValueLabel label="Curr Read Date" value={meter.currReadDate} />
        <ValueLabel label="Consumption" value={meter.billedConsumption} />
      </div>

      {/* Correction Form - Show only when toggle is ON */}
      {isCorrecting && (
        <div className="animate-in fade-in slide-in-from-top-2 duration-300 space-y-6 p-4 border border-blue-100 bg-blue-50/10 rounded-lg">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700">
              Note: The corrected reading date must fall within the original
              billing period.
            </p>
          </div>

          <DynamicForm
            fields={getFormFields(meter)}
            form={form}
            gridColumns={5}
            responsiveColumns={{ sm: 1, md: 2, lg: 5 }}
            config={{
              layout: "vertical",
              spacing: "small",
              showSubmitButton: false,
              showResetButton: false,
              classes: FIELD_CLASSES,
            }}
            onFieldChange={(field, value) => handleFieldChange(field, value)}
          />

          {/* Warnings */}
          {isServiceDaysExceeded && (
            <div className="p-2 bg-orange-50 border border-orange-100 rounded-md flex items-start gap-2 text-xs text-orange-700">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Service days ({serviceDays}) exceed standard cycle (
                {billingCycleDays} days).
              </span>
            </div>
          )}

          {meterStatus === "2" && (
            <div className="p-2 bg-yellow-50 border border-yellow-100 rounded-md flex items-start gap-2 text-xs text-yellow-700">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>RCNT status selected. Consumption set to 0.</span>
            </div>
          )}
        </div>
      )}

      {index < totalMeters - 1 && <Separator className="my-6" />}
    </div>
  );
};

export default function ConsumptionCorrectionForm({
  billDetails,
  onSave,
  onFormEdit,
  isSaved,
  correctionId,
  savedData
}) {
  const { mutate: correctConsumption } = usePatchBillCorrection();
  const [meterStates, setMeterStates] = useState({});
  const [meterForms, setMeterForms] = useState({});
  const [hasEdited, setHasEdited] = useState(false);

  const billingCycleDays = 30;

  // Extract utility meters from billDetails
  const utilityMeters = useMemo(() => {
    if (!billDetails?.utilityServiceMeterDetails) return [];
    interface UtilityMeter {
      meterNumber?: string;
      previousReading: number;
      currentReading: number;
      consumption: number;
      previousReadingDate: string;
      currentReadingDate: string;
      meterId?: string;
    }

    type UtilityServiceMeterDetails = Record<string, UtilityMeter[]>;
    const meterDetails =
      billDetails.utilityServiceMeterDetails as UtilityServiceMeterDetails;

    return Object.entries(meterDetails).flatMap(([utilityType, meterArray]) =>
      meterArray.map((meter, index) => ({
        id: meter.meterId,
        type: utilityType,
        meterNo: meter.meterNumber,
        prevReading: meter.previousReading,
        currReading: meter.currentReading,
        billedConsumption: meter.consumption,
        prevReadDate: meter.previousReadingDate,
        currReadDate: meter.currentReadingDate,
      }))
    );
  }, [billDetails]);

  // Initialize meter states from saved data
  useEffect(() => {
    if (savedData?.meters) {
      const initialStates = {};
      savedData.meters.forEach(savedMeter => {
        initialStates[savedMeter.meter_id] = {
          isCorrecting: true
        };
      });
      setMeterStates(initialStates);
    }
  }, [savedData]);

  const toggleCorrection = (meterId, checked) => {
    if (isSaved && !hasEdited) {
      setHasEdited(true);
      onFormEdit();
    }
    setMeterStates((prev) => ({
      ...prev,
      [meterId]: { ...prev[meterId], isCorrecting: checked },
    }));
  };

  const handleFormReady = (meterId, form) => {
    setMeterForms((prev) => ({
      ...prev,
      [meterId]: form,
    }));
  };

  const handleMeterFormEdit = () => {
    if (isSaved && !hasEdited) {
      setHasEdited(true);
      onFormEdit();
    }
  };

  const handleSaveAll = async () => {
    const metersArray = [];
    let hasErrors = false;

    // Validate and collect data from all active forms
    for (const meter of utilityMeters) {
      const isActive = meterStates[meter.id]?.isCorrecting;
      if (isActive) {
        const form = meterForms[meter.id];
        if (form) {
          const isValid = await FormService.validateForm(form);
          if (!isValid) {
            hasErrors = true;
          } else {
            const formData = form.getValues();
            // Create meter object for payload
            metersArray.push({
              meter_id: meter.id,
              corrected_reading: Number(formData.correctedCurrent) || 0,
              reading_status: formData.status,
              reading_date: formData.readingDate,
              reason: formData.reason,
              consumption: formData.consumption,
              serviceDays: formData.serviceDays,
            });
          }
        }
      }
    }

    if (!hasErrors && metersArray.length > 0) {
      const payload = {
        meters: metersArray,
      };
      
      correctConsumption({
        id: correctionId,
        type: 'consumption',
        payload,
      }, {
        onSuccess: () => {
          // Pass the data structure to parent for saving to stepHelper
          const saveData = {
            meters: metersArray
          };
          setHasEdited(false);
          onSave(saveData);
        }
      });
    }
  };

  // Get saved data for specific meter
  const getSavedMeterData = (meterId: string) => {
    console.log('debug meter id', meterId)
    if (!savedData?.meters) return null;
    return savedData.meters.find(m => m.meter_id === meterId);
  };

  return (
    <Card className="border-blue-100 shadow-sm">
      <CardHeader className="bg-blue-50/50 border-b border-blue-100 p-2">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Gauge className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <CardTitle>Consumption Correction</CardTitle>
            <p className="text-xs text-gray-600 font-normal">
              Adjust meter readings and consumption
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-6 flex items-start gap-2">
          <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-700">
            Previous bill current reading will be marked as superseded (this
            record was valid at the time but has been replaced by a newer,
            correct record). The new current reading will be marked as actual
            for VEE status.
          </p>
        </div>

        <div className="space-y-8">
          {utilityMeters.map((meter, index) => {
            const isCorrecting = meterStates[meter.id]?.isCorrecting || false;
            const savedMeterData = getSavedMeterData(meter.id);

            return (
              <MeterCorrectionCard
                key={meter.id}
                meter={meter}
                index={index}
                totalMeters={utilityMeters.length}
                billingCycleDays={billingCycleDays}
                isCorrecting={isCorrecting}
                onToggleCorrection={toggleCorrection}
                onFormReady={handleFormReady}
                savedMeterData={savedMeterData}
                onFormEdit={handleMeterFormEdit}
              />
            );
          })}
        </div>

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
          <Button 
            onClick={handleSaveAll}
            className={cn(
              "min-w-[140px]",
              isSaved
                ? "bg-green-600 hover:bg-green-700"
                : "bg-blue-600 hover:bg-blue-700"
            )}
          >
            {isSaved ? (
              <>
                <Check className="mr-2 h-4 w-4" />
                Saved
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}